/**
 * Dialog Form Kit Validation Framework
 *
 * Field-level validation utilities for form components.
 * All validation is handled by individual field components, NOT by SmartSection.
 *
 * IMPORTANT: Uses theme tokens for error styling - NO hardcoded values.
 */

// ============================================================================
// VALIDATION TYPES
// ============================================================================

export type ValidationType = 'required' | 'minLength' | 'maxLength' | 'pattern' | 'email' | 'url' | 'number' | 'custom';

export interface ValidationRule {
  type: ValidationType;
  value?: any;
  message: string;
  validator?: (value: any, data?: any) => boolean | Promise<boolean>;
  async?: boolean;
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
  value: any;
  timestamp?: Date;
}

// ============================================================================
// BUILT-IN VALIDATORS
// ============================================================================

/**
 * Built-in validation functions
 */
export const validators = {
  required: (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value);
  },

  minLength: (value: string, minLength: number): boolean => {
    if (!value) return true; // Empty values are handled by required validator
    return value.length >= minLength;
  },

  maxLength: (value: string, maxLength: number): boolean => {
    if (!value) return true; // Empty values are handled by required validator
    return value.length <= maxLength;
  },

  pattern: (value: string, pattern: RegExp): boolean => {
    if (!value) return true; // Empty values are handled by required validator
    return pattern.test(value);
  },

  email: (value: string): boolean => {
    if (!value) return true; // Empty values are handled by required validator
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  },

  url: (value: string): boolean => {
    if (!value) return true; // Empty values are handled by required validator
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  },

  number: (value: any): boolean => {
    if (value === null || value === undefined || value === '') return true;
    return !isNaN(Number(value)) && isFinite(Number(value));
  }
};

// ============================================================================
// VALIDATION FRAMEWORK
// ============================================================================

/**
 * Validate a field value against an array of validation rules
 *
 * @param value - The field value to validate
 * @param rules - Array of validation rules to apply
 * @param fieldName - Name of the field being validated
 * @returns Array of validation errors (empty if valid)
 */
export const validateField = (
  value: any,
  rules: ValidationRule[],
  fieldName: string = 'field'
): ValidationError[] => {
  const errors: ValidationError[] = [];

  for (const rule of rules) {
    let isValid = false;

    switch (rule.type) {
      case 'required':
        isValid = validators.required(value);
        break;
      case 'minLength':
        isValid = validators.minLength(value, rule.value);
        break;
      case 'maxLength':
        isValid = validators.maxLength(value, rule.value);
        break;
      case 'pattern':
        isValid = validators.pattern(value, rule.value);
        break;
      case 'email':
        isValid = validators.email(value);
        break;
      case 'url':
        isValid = validators.url(value);
        break;
      case 'number':
        isValid = validators.number(value);
        break;
      case 'custom':
        if (rule.validator) {
          const result = rule.validator(value);
          isValid = result instanceof Promise ? false : result;
        }
        break;
      default:
        console.warn(`Unknown validation rule type: ${rule.type}`);
        continue;
    }

    if (!isValid) {
      errors.push({
        field: fieldName,
        rule: rule.type,
        message: rule.message,
        value,
        timestamp: new Date()
      });
    }
  }

  return errors;
};

/**
 * Validate a field value asynchronously
 *
 * @param value - The field value to validate
 * @param rules - Array of validation rules to apply
 * @param fieldName - Name of the field being validated
 * @returns Promise that resolves to array of validation errors
 */
export const validateFieldAsync = async (
  value: any,
  rules: ValidationRule[],
  fieldName: string = 'field'
): Promise<ValidationError[]> => {
  const errors: ValidationError[] = [];

  for (const rule of rules) {
    let isValid = false;

    if (rule.async && rule.validator) {
      try {
        isValid = await rule.validator(value);
      } catch (error) {
        console.error('Async validation error:', error);
        isValid = false;
      }
    } else {
      // Fall back to synchronous validation
      const syncErrors = validateField(value, [rule], fieldName);
      if (syncErrors.length > 0) {
        errors.push(...syncErrors);
        continue;
      }
      isValid = true;
    }

    if (!isValid) {
      errors.push({
        field: fieldName,
        rule: rule.type,
        message: rule.message,
        value,
        timestamp: new Date()
      });
    }
  }

  return errors;
};

/**
 * Validate an entire form object
 *
 * @param data - Object containing form field values
 * @param rules - Object mapping field names to validation rules
 * @returns Array of validation errors for all fields
 */
export const validateForm = (
  data: Record<string, any>,
  rules: Record<string, ValidationRule[]>
): ValidationError[] => {
  const allErrors: ValidationError[] = [];

  for (const [fieldName, fieldRules] of Object.entries(rules)) {
    const fieldValue = data[fieldName];
    const fieldErrors = validateField(fieldValue, fieldRules, fieldName);
    allErrors.push(...fieldErrors);
  }

  return allErrors;
};

/**
 * Validate an entire form object asynchronously
 *
 * @param data - Object containing form field values
 * @param rules - Object mapping field names to validation rules
 * @returns Promise that resolves to array of validation errors for all fields
 */
export const validateFormAsync = async (
  data: Record<string, any>,
  rules: Record<string, ValidationRule[]>
): Promise<ValidationError[]> => {
  const allErrors: ValidationError[] = [];

  for (const [fieldName, fieldRules] of Object.entries(rules)) {
    const fieldValue = data[fieldName];
    const fieldErrors = await validateFieldAsync(fieldValue, fieldRules, fieldName);
    allErrors.push(...fieldErrors);
  }

  return allErrors;
};

// ============================================================================
// CUSTOM VALIDATOR REGISTRY
// ============================================================================

/**
 * Registry for custom validators
 */
class CustomValidatorRegistry {
  private validators = new Map<string, Function>();

  /**
   * Register a custom validator
   */
  register(name: string, validator: Function): void {
    this.validators.set(name, validator);
  }

  /**
   * Get a custom validator by name
   */
  get(name: string): Function | undefined {
    return this.validators.get(name);
  }

  /**
   * Remove a custom validator
   */
  remove(name: string): boolean {
    return this.validators.delete(name);
  }

  /**
   * List all registered validator names
   */
  list(): string[] {
    return Array.from(this.validators.keys());
  }
}

export const customValidators = new CustomValidatorRegistry();

// ============================================================================
// COMMON VALIDATION RULES
// ============================================================================

/**
 * Pre-built validation rule factories for common use cases
 */
export const validationRules = {
  required: (message: string = 'This field is required'): ValidationRule => ({
    type: 'required',
    message
  }),

  minLength: (length: number, message?: string): ValidationRule => ({
    type: 'minLength',
    value: length,
    message: message || `Must be at least ${length} characters`
  }),

  maxLength: (length: number, message?: string): ValidationRule => ({
    type: 'maxLength',
    value: length,
    message: message || `Must be no more than ${length} characters`
  }),

  email: (message: string = 'Please enter a valid email address'): ValidationRule => ({
    type: 'email',
    message
  }),

  url: (message: string = 'Please enter a valid URL'): ValidationRule => ({
    type: 'url',
    message
  }),

  pattern: (regex: RegExp, message: string): ValidationRule => ({
    type: 'pattern',
    value: regex,
    message
  }),

  custom: (validator: (value: any, data?: any) => boolean | Promise<boolean>, message: string, async: boolean = false): ValidationRule => ({
    type: 'custom',
    validator,
    message,
    async
  })
};

// ============================================================================
// EXPORTS
// ============================================================================
// All exports are declared inline above

